import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeRequest } from "../../../utils/axios.js"; 
import { generateRSAKeyPair, exportPublicKey, exportPrivateKey, encryptPrivateKeyWithPassword } from '../../../utils/cryptoUtils';
import { ethers } from 'ethers';

export const useAuthRegister = () => {
    const [inputs, setInputs] = useState({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        name: "",
    });

    const [err, setErr] = useState(null);
    const [isRegistering, setIsRegistering] = useState(false);
    const navigate = useNavigate(); 

    const handleChange = (e) => {
        setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleCheckEmail = async () => {
        if (!inputs.email) return; 

        try {
            await makeRequest.post("/auth/check-email", { email: inputs.email });
            if (err === "This email address is already in use!") {
                setErr(null); 
        }
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setErr(error.response.data);
            }
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault(); 

        if (inputs.password !== inputs.confirmPassword) {
            return setErr("Passwords do not match!");
        }
        
        setIsRegistering(true);
        setErr(null);
        
        try {
            const keyPair = await generateRSAKeyPair();
            const publicKeyBase64 = await exportPublicKey(keyPair.publicKey);
            const privateKeyBase64 = await exportPrivateKey(keyPair.privateKey);

            const encryptedPrivateKey = await encryptPrivateKeyWithPassword(privateKeyBase64, inputs.password);

            const dataToSend = {
                ...inputs,
                publicKey: publicKeyBase64,
                encryptedPrivateKey: encryptedPrivateKey
            };

            await makeRequest.post("/auth/register", dataToSend);

            localStorage.setItem("myPublicKey", publicKeyBase64);
            // localStorage.setItem("myPrivateKey", privateKeyBase64);
            // localStorage.setItem(`privateKey_${inputs.username}`, privateKeyBase64);

            navigate("/login"); 
        } 
        catch (err) {
            const errorMessage = err.response?.data?.error 
                                || err.response?.data?.message 
                                || (typeof err.response?.data === 'string' ? err.response?.data : "Registration failed. Please try again.");
            setErr(errorMessage);
        }
        finally {
            setIsRegistering(false);
        }
    };

    const handleWeb3Register = async (e) => {
        e.preventDefault();
        
        if (!inputs.username || !inputs.password || !inputs.name) {
            return setErr("Username, Name and Password (as Security PIN) are required for Web3!");
        }

        setIsRegistering(true);
        setErr(null);

        try {
            if (!window.ethereum) throw new Error("Please install MetaMask!");
            const provider = new ethers.BrowserProvider(window.ethereum);
            const accounts = await provider.send("eth_requestAccounts", []);
            const walletAddress = accounts[0];

            const signer = await provider.getSigner();
            const message = `I am registering a Nexia account for username: ${inputs.username}`;
            const signature = await signer.signMessage(message);

            const keyPair = await generateRSAKeyPair();
            const publicKeyBase64 = await exportPublicKey(keyPair.publicKey);
            const privateKeyBase64 = await exportPrivateKey(keyPair.privateKey);
            
            const encryptedPrivateKey = await encryptPrivateKeyWithPassword(privateKeyBase64, inputs.password);

            const dataToSend = {
                username: inputs.username,
                email: inputs.email, 
                name: inputs.name,
                password: inputs.password, 
                wallet_address: walletAddress,
                publicKey: publicKeyBase64,
                encryptedPrivateKey: encryptedPrivateKey,
                signature: signature,
                message: message
            };

            await makeRequest.post("/auth/web3/register", dataToSend);
            
            navigate("/login"); 
        } 
        catch (err) {
            console.error(err);
            if (err.code === 4001) {
                setErr("You rejected the signature request in MetaMask.");
            } else {
                const errorMessage = err.response?.data?.error 
                                  || err.response?.data 
                                  || "Web3 Registration failed. Please try again.";
                setErr(errorMessage);
            }
        }
        finally {
            setIsRegistering(false);
        }
    };

    return {
        inputs,
        err,
        isRegistering,
        handleChange,
        handleCheckEmail,
        handleRegister,
        handleWeb3Register 
    };
};